import { useEffect, useState } from "react";
import { useHomeUi } from "../../context/HomeUiContext";
import { useToys } from "./ToysContext";

export default function QuantitySelector({
  toy,
  withConfirm = false,
  setIsOpen = () => {},
  mode = "default",
}) {
  const { openDrawer, showStockLimit, remindSelectRentalDate } = useHomeUi();
  const { setCart, cart, toys, reservationDate, availabilityByToyId } =
    useToys();

  const toyName = toy.fields["Name"] || "this item";

  const maxQty =
    reservationDate && availabilityByToyId.hasOwnProperty(toy.id)
      ? availabilityByToyId[toy.id]
      : undefined;
  const isUnavailable = maxQty === 0;

  const cartItem = cart.find((item) => item.toy.id === toy.id);
  const initialQuantity = cartItem ? cartItem.quantity : 0;

  const [tempQuantity, setTempQuantity] = useState(initialQuantity);

  useEffect(() => {
    if (maxQty !== undefined && initialQuantity > maxQty) {
      setTempQuantity(maxQty);
    } else {
      setTempQuantity(initialQuantity);
    }
  }, [initialQuantity, maxQty]);

  const updateCart = (newQuantity) => {
    const cappedQuantity =
      maxQty !== undefined
        ? Math.min(Math.max(0, newQuantity), maxQty)
        : Math.max(0, newQuantity);

    setCart((prevCart) => {
      const wasEmpty = prevCart.length === 0;
      let updatedCart = [...prevCart];
      const existingItemIndex = updatedCart.findIndex(
        (cartItem) => cartItem.toy.id === toy.id,
      );

      if (existingItemIndex !== -1) {
        if (cappedQuantity > 0) {
          updatedCart[existingItemIndex].quantity = cappedQuantity;
        } else {
          updatedCart = updatedCart.filter(
            (_, idx) => idx !== existingItemIndex,
          );
        }
      } else if (cappedQuantity > 0) {
        updatedCart.push({ toy, quantity: cappedQuantity });
      }

      updatedCart.sort((a, b) => {
        const indexA = toys.findIndex((m) => m.id === a.toy.id);
        const indexB = toys.findIndex((m) => m.id === b.toy.id);
        return indexA - indexB;
      });

      if (wasEmpty && updatedCart.length > 0) {
        queueMicrotask(() => openDrawer());
      }

      return updatedCart;
    });
    setIsOpen(false);
  };

  const handleImmediateChange = (newQuantity) => {
    if (
      newQuantity > initialQuantity &&
      maxQty !== undefined &&
      newQuantity > maxQty
    ) {
      showStockLimit(toyName, maxQty);
      return;
    }
    if (newQuantity >= 0) {
      updateCart(newQuantity);
    }
  };

  const handleTempChange = (change) => {
    setTempQuantity((prev) => {
      if (change > 0 && maxQty !== undefined && prev >= maxQty) {
        showStockLimit(toyName, maxQty);
        return prev;
      }
      const next = prev + change;
      const floor = Math.max(0, next);
      if (maxQty !== undefined && floor > maxQty) {
        showStockLimit(toyName, maxQty);
        return maxQty;
      }
      return floor;
    });
  };

  const atZero = withConfirm ? tempQuantity === 0 : initialQuantity === 0;
  const dateNotSelected = !reservationDate;

  if (isUnavailable) {
    return (
      <p className="text-xs text-gray-500 italic">
        Unavailable for this date
      </p>
    );
  }

  if (mode === "card") {
    return (
      <div
        className={`card-foot-row${dateNotSelected ? " opacity-50" : ""}`}
        aria-disabled={dateNotSelected}
      >
        <div className="qty">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (dateNotSelected) return;
              handleImmediateChange(initialQuantity - 1);
            }}
            disabled={atZero || dateNotSelected}
            className="qbtn"
          >
            −
          </button>
          <span className="qnum">{initialQuantity}</span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (dateNotSelected) {
                remindSelectRentalDate();
                return;
              }
              if (maxQty !== undefined && initialQuantity >= maxQty) {
                showStockLimit(toyName, maxQty);
                return;
              }
              handleImmediateChange(initialQuantity + 1);
            }}
            className="qbtn"
          >
            +
          </button>
        </div>
        <button
          type="button"
          className={`add-btn${initialQuantity > 0 ? " in" : ""}`}
          onClick={(e) => {
            e.stopPropagation();
            if (dateNotSelected) {
              remindSelectRentalDate();
              return;
            }
            if (maxQty !== undefined && initialQuantity >= maxQty) {
              showStockLimit(toyName, maxQty);
              return;
            }
            handleImmediateChange(initialQuantity + 1);
          }}
        >
          {initialQuantity > 0 ? "✓ Added" : "+ Add"}
        </button>
      </div>
    );
  }

  return (
    <div
      className={`qty-row ${dateNotSelected ? "pointer-events-none opacity-50" : ""}`}
      aria-disabled={dateNotSelected}
    >
      <div className="qty">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (dateNotSelected) return;
            withConfirm
              ? handleTempChange(-1)
              : handleImmediateChange(initialQuantity - 1);
          }}
          disabled={atZero || dateNotSelected}
          className="qbtn"
        >
          −
        </button>
        <span className="qnum">
          {withConfirm ? tempQuantity : initialQuantity}
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (dateNotSelected) return;
            if (withConfirm) {
              handleTempChange(1);
            } else {
              if (maxQty !== undefined && initialQuantity >= maxQty) {
                showStockLimit(toyName, maxQty);
                return;
              }
              handleImmediateChange(initialQuantity + 1);
            }
          }}
          disabled={dateNotSelected}
          className="qbtn"
        >
          +
        </button>
      </div>

      {withConfirm && (
        <button
          type="button"
          className="add-btn disabled:opacity-50"
          onClick={() => !dateNotSelected && updateCart(tempQuantity)}
          disabled={dateNotSelected}
        >
          Add to selection
        </button>
      )}
    </div>
  );
}
